import React, { useState, useRef, useEffect } from 'react';

const MultiSelectDropdown = ({ options = [], value = [], onChange, placeholder = 'Select...', label }) => {
  const [open, setOpen] = useState(false);
  const ref = useRef(null);

  useEffect(() => {
    const handleClickOutside = (e) => {
      if (ref.current && !ref.current.contains(e.target)) {
        setOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const toggle = (optionValue) => {
    if (value.includes(optionValue)) {
      onChange(value.filter((v) => v !== optionValue));
    } else {
      onChange([...value, optionValue]);
    }
  };

  const displayLabel = () => {
    if (value.length === 0) return placeholder;
    if (value.length === 1) {
      const match = options.find((o) => String(o.value) === String(value[0]));
      return match ? match.label : placeholder;
    }
    return `${value.length} selected`;
  };

  return (
    <div ref={ref} className="relative w-full">
      <button
        type="button"
        onClick={() => setOpen((prev) => !prev)}
        className="w-full border rounded px-3 py-2 text-sm text-left bg-white flex items-center justify-between gap-2 focus:outline-none focus:ring-2 focus:ring-blue-300"
      >
        <span className={value.length === 0 ? 'text-gray-400' : 'text-gray-800'}>
          {displayLabel()}
        </span>
        <svg
          className={`w-4 h-4 text-gray-400 flex-shrink-0 transition-transform ${open ? 'rotate-180' : ''}`}
          viewBox="0 0 20 20"
          fill="currentColor"
        >
          <path fillRule="evenodd" d="M5.293 7.293a1 1 0 011.414 0L10 10.586l3.293-3.293a1 1 0 111.414 1.414l-4 4a1 1 0 01-1.414 0l-4-4a1 1 0 010-1.414z" clipRule="evenodd" />
        </svg>
      </button>

      {open && (
        <div className="absolute z-50 mt-1 w-full bg-white border border-gray-200 rounded shadow-lg">
          {value.length > 0 && (
            <div className="px-3 py-1.5 border-b border-gray-100">
              <button
                type="button"
                onClick={() => onChange([])}
                className="text-xs text-blue-600 hover:underline"
              >
                Clear all
              </button>
            </div>
          )}
          <ul className="py-1 max-h-96 overflow-y-auto">
            {options.map((option) => {
              const checked = value.includes(String(option.value));
              return (
                <li key={option.value}>
                  <label className="flex items-center gap-2 px-3 py-1.5 hover:bg-gray-50 cursor-pointer text-sm text-gray-700 select-none">
                    <input
                      type="checkbox"
                      checked={checked}
                      onChange={() => toggle(String(option.value))}
                      className="accent-blue-600 w-4 h-4 flex-shrink-0"
                    />
                    {option.label}
                  </label>
                </li>
              );
            })}
            {options.length === 0 && (
              <li className="px-3 py-2 text-sm text-gray-400">No options</li>
            )}
          </ul>
        </div>
      )}
    </div>
  );
};

export default MultiSelectDropdown;
